import React, { useState, useEffect } from "react";

import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { JobType } from "../../../../../../../../models/jobType";
import { PriceGroup } from "../../../../../../../../models/price.group";
import PriceGroupService from "../../../../../../../../services/price.group.service";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Status } from "../../../../../../../../util/enums";
import AutoSuggest from "../../../../../../../../components/Form/AutoSuggest";
import Input from "../../../../../../../../components/Form/Input";
import Modal from "../../../../../../../../components/Generic/Modal";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import useStyles from "./styles";

interface Props {
  jobType?: JobType;
  open: boolean;
  hasError?: boolean;
  isLoading?: boolean;
  closeModal: () => void;
  updateOrCreateJobType: (jobType: any) => void;
}

const OrderTypeModal = ({
  jobType,
  open,
  hasError,
  isLoading = false,
  closeModal,
  updateOrCreateJobType,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [description, setDescription] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [groups, setGroups] = useState<PriceGroup[]>([]);
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const [allGroups, setAllGroups] = useState<PriceGroup[]>([]);

  const { isFetching: loadingGroups } = useQuery(
    ["groups.list"],
    () =>
      PriceGroupService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: open,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setAllGroups(response.data);
        }
      },
    }
  );

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    description: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (description) =>
        description ? description !== "" : false
      ),
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (type) =>
        type ? type !== "" : false
      ),
  });

  const clearFields = () => {
    setDescription("");
    setName("");
    setStatus(Status.INACTIVE);
    setGroups([]);
  };

  const isValid = (jobType: any) => {
    try {
      schema.validateSync(
        {
          name: jobType.name,
          description: jobType.description,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const object = {
      name,
      description,
      status,
      groups,
    };

    if (isValid(object)) updateOrCreateJobType(object);
  };

  useEffect(() => {
    if (jobType) {
      setName(jobType?.name);
      setDescription(jobType?.description);
      setStatus(jobType?.status);
      setGroups(jobType?.groups || []);
    }
  }, [jobType]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const object = {
        name,
        description,
      };

      isValid(object);
    }
  }, [name, description, hasFormBeenSubmitted]);

  return (
    <Modal
      data-cy="jobTypeModal"
      open={open}
      handleClose={closeModal}
      title={jobType ? translate("editOrderType") : translate("createNewOrderType")}
      className={classes.modalWidth}
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        loading: isLoading,
        saveDisabled: isLoading,
        textSave: jobType ? translate("update") : translate("create"),
        dataCySave: "submitButton",
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("orderTypeStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="name"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!getErrorByField("name")}
              errorMessage={getErrorByField("name")}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="description"
              label={translate("description")}
              placeholder={translate("description")}
              error={!!getErrorByField("description")}
              errorMessage={getErrorByField("description")}
              value={description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            />
          </Grid>
        </Grid>
        <Grid container className="mt-4">
          <SkeletonComponent isLoading={loadingGroups}>
            <Grid item xs={12} md={12} lg={12}>
              <AutoSuggest
                tagDataCy="item-group"
                disabled={isLoading}
                data-cy="groupSuggest"
                label={translate("groupCosting")}
                placeholder={translate("searchGroups")}
                options={allGroups}
                value={groups}
                getOptionLabel={(group: PriceGroup) => (group ? group.name || "" : "")}
                getOptionSelected={(option: PriceGroup, value: PriceGroup) =>
                  option.id === value.id
                }
                multiple
                onChange={(groups: PriceGroup[]) => setGroups(groups)}
              />
            </Grid>
          </SkeletonComponent>
        </Grid>
      </section>
    </Modal>
  );
};

export default OrderTypeModal;
